import { useCallback } from "react";
import { ApolloError } from "@apollo/client";
import {
  useUpdateCart,
  UpdateCartData,
  UpdateCartProps,
} from "src/hooks/useUpdateCart";
import { CartInfo } from "src/types/cartInfo";
import type { Address, Checkout, CheckoutUser } from "src/types/forms";

type WithNull<T> = {
  [P in keyof T]: T[P] | null;
};

type CheckoutAddressWithNull = WithNull<Address>;

interface UpdateCartCountryHook {
  data?: UpdateCartData;
  loading: boolean;
  error?: ApolloError;
  updateCartCountry: (checkout: Checkout, cartInfo: CartInfo) => void;
}

function useUpdateCartCountry({
  refetchQueries = [],
  onCompleted,
  onError,
}: UpdateCartProps = {}): UpdateCartCountryHook {
  const { updateCart, loading, error } = useUpdateCart({
    refetchQueries,
    onCompleted,
    onError,
  });

  const updateCartCountry: UpdateCartCountryHook["updateCartCountry"] =
    useCallback(
      (checkout: Checkout, cartInfo: CartInfo) => {
        const address: CheckoutAddressWithNull & CheckoutUser = {
          firstName: checkout.firstName,
          lastName: checkout.lastName,
          email: checkout.email,
          phone: checkout.phone,
          streetName: null,
          streetNumber: null,
          additionalStreetInfo: null,
          postalCode: null,
          city: null,
          state: null,
          country: checkout.shippingAddress.country,
        };

        void updateCart({
          cartInfo,
          actions: [
            {
              setShippingMethod: {
                shippingMethod: null,
              },
            },
            {
              setShippingAddress: {
                address,
              },
            },
            {
              recalculate: {
                updateProductData: true,
              },
            },
          ],
        });
      },
      [updateCart]
    );

  return { updateCartCountry, loading, error };
}

export { useUpdateCartCountry };
